// In-browser DICOM header parsing for `.dcm` uploads: the embedded tags
// PREFILL the upload form, exactly like a dropped metadata file's contents.
// Best-effort and display-only: what the form says is what is saved, so a
// parse failure here only means an unprefilled form.

import { EMPTY_DRAFT, splitPn, toDateInput, toSex, type MetadataDraft } from './sidecar';

const HEAD_BYTES = 8 * 1024 * 1024; // identity tags precede pixel data
const FULL_PARSE_MAX = 64 * 1024 * 1024;

type NaturalDataset = Record<string, unknown>;

function str(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) return str(v[0]);
  if (typeof v === 'object' && 'Alphabetic' in (v as Record<string, unknown>)) {
    return str((v as Record<string, unknown>).Alphabetic);
  }
  return String(v);
}

function toDateTimeInput(date: string, time: string): string {
  const d = toDateInput(date);
  if (!d) return '';
  const t = /^\d{4}/.test(time) ? `${time.slice(0, 2)}:${time.slice(2, 4)}` : '00:00';
  return `${d}T${t}`;
}

function mapDataset(ds: NaturalDataset): MetadataDraft {
  const d: MetadataDraft = { ...EMPTY_DRAFT };
  [d.patientFamily, d.patientGiven] = splitPn(str(ds.PatientName));
  d.patientId = str(ds.PatientID);
  d.patientIdIssuer = str(ds.IssuerOfPatientID);
  d.patientBirthDate = toDateInput(str(ds.PatientBirthDate));
  d.patientSex = toSex(str(ds.PatientSex));
  d.accession = str(ds.AccessionNumber);
  d.studyId = str(ds.StudyID);
  d.studyDescription = str(ds.StudyDescription);
  d.studyDateTime = toDateTimeInput(str(ds.StudyDate), str(ds.StudyTime));
  d.referringPhysician = str(ds.ReferringPhysicianName).replace('^', ', ');
  d.modality = str(ds.Modality) || d.modality;
  d.seriesDescription = str(ds.SeriesDescription);
  d.manufacturer = str(ds.Manufacturer);
  d.stationName = str(ds.StationName);
  d.deviceSerial = str(ds.DeviceSerialNumber);
  d.containerId = str(ds.ContainerIdentifier);
  const spec = Array.isArray(ds.SpecimenDescriptionSequence)
    ? (ds.SpecimenDescriptionSequence[0] as NaturalDataset | undefined)
    : undefined;
  if (spec) {
    d.specimenId = str(spec.SpecimenIdentifier);
    d.specimenDescription = str(spec.SpecimenShortDescription);
  }
  return d;
}

/** Parse a .dcm File's header into a draft; null when unparseable. */
export async function parseDicomFile(file: File): Promise<MetadataDraft | null> {
  try {
    const dcmjs = (await import('dcmjs')) as unknown as {
      data: {
        DicomMessage: { readFile: (b: ArrayBuffer, o?: object) => { dict: object } };
        DicomMetaDictionary: { naturalizeDataset: (d: object) => NaturalDataset };
      };
    };
    const tryParse = async (bytes: ArrayBuffer): Promise<NaturalDataset | null> => {
      try {
        const msg = dcmjs.data.DicomMessage.readFile(bytes, { ignoreErrors: true });
        return dcmjs.data.DicomMetaDictionary.naturalizeDataset(msg.dict);
      } catch {
        return null;
      }
    };
    let ds = await tryParse(await file.slice(0, Math.min(file.size, HEAD_BYTES)).arrayBuffer());
    if ((!ds || !ds.PatientName) && file.size > HEAD_BYTES && file.size <= FULL_PARSE_MAX) {
      ds = await tryParse(await file.arrayBuffer());
    }
    if (!ds) return null;
    const draft = mapDataset(ds);
    // A dataset with no identity at all still counts as parsed (ANON export);
    // the form simply starts empty and the operator fills or anonymizes.
    return draft;
  } catch {
    return null;
  }
}
